import { useNavigate } from 'react-router-dom'

import { UserType } from '@hapana/shared/web/services/gqty/schema.generated'

import { getMinutesFromValue } from '_pages/settings/location-settings/location-operations/components/operations/auto-logout/auto-logout.constants'
import { signOut } from '_services/auth.service'
import { useStaffSessionPolicy } from '_hooks/staff-session-policy.hooks'
import { FeatureFlagKeys, useFeatureFlag } from '_hooks/useFeatureFlag'

import { ROUTES } from '../../app.constants'

import IdleWarningDialog from './idle-warning-dialog.component'
import { IDLE_WARNING_SECONDS, resolveIdleTimeoutMinutes } from './idle-session.utils'
import { useIdleSignOut } from './use-idle-sign-out.hook'

type IdleSessionGuardProps = {
  siteId: string | null | undefined
  locationAutoLogout: boolean
  locationAutoLogoutMinutes: string
  userType: string | null | undefined
  isImpersonating: boolean
}

const IdleSessionGuard = ({
  siteId,
  locationAutoLogout,
  locationAutoLogoutMinutes,
  userType,
  isImpersonating,
}: IdleSessionGuardProps) => {
  const navigate = useNavigate()
  const { value: isSessionPolicyFlagOn } = useFeatureFlag(
    FeatureFlagKeys.StaffSessionPolicyEnabled,
    { defaultValue: false }
  )
  const isSessionPolicyEnabled = isSessionPolicyFlagOn === true
  // The user type must be KNOWN before the timer may run: while `me` loads (or if it fails) it is
  // undefined, and treating that as "not an admin" would start signing Hapana admins out.
  const isEligible = !!userType && userType !== UserType.admin && !isImpersonating
  const { data: policy } = useStaffSessionPolicy({ enabled: isSessionPolicyEnabled && isEligible })

  const timeoutMinutes = isEligible
    ? resolveIdleTimeoutMinutes({
        isSessionPolicyEnabled,
        policy,
        isLocationPage: !!siteId,
        locationAutoLogoutEnabled: locationAutoLogout,
        locationAutoLogoutMinutes: getMinutesFromValue(locationAutoLogoutMinutes),
      })
    : null

  const signOutForInactivity = async () => {
    await signOut()
    navigate(ROUTES.login.path, { replace: true, state: { inactivityLogout: true } })
  }

  const { isWarning, secondsRemaining, staySignedIn } = useIdleSignOut({
    timeoutMinutes,
    warningSeconds: isSessionPolicyEnabled ? IDLE_WARNING_SECONDS : 0,
    onExpire: () => {
      void signOutForInactivity()
    },
  })

  return (
    <IdleWarningDialog
      open={isWarning}
      secondsRemaining={secondsRemaining}
      onStaySignedIn={staySignedIn}
      onSignOut={() => {
        void signOutForInactivity()
      }}
    />
  )
}

export default IdleSessionGuard
